import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Chip, Tab, Tabs } from '@mui/material'
import { SyntheticEvent, useCallback, useMemo } from 'react'

import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp'
import { MuiTabPanel } from '@/components/MuiTab'
import { a11yProps } from '@/components/MuiTab/helpers'
import { useAppContext, useAppStore } from '@/hooks'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { isLocked, isLockedByMe } from '@/utils/locking'

import { useTabKeyboardNav } from './hooks/useTabKeyboardNav'
import { ElementTabsV2Props } from './types'

/**
 * Generic tab container for element detail pages.
 * 
 * Features:
 * - MUI Tabs with scrollable overflow
 * - Keyboard navigation (Ctrl+PgUp/PgDn)
 * - Status chip (Editing/Locked)
 * - Help button for keyboard shortcuts
 * 
 * @example
 * ```tsx
 * <ElementTabsV2
 *   tabs={[
 *     { id: 'overview', labelKey: 'TAB_OVERVIEW', component: <Overview /> },
 *     { id: 'columns', labelKey: 'TAB_COLUMNS', component: <Columns /> },
 *   ]}
 *   selectedTab={selectedTab}
 *   onTabChange={setSelectedTab}
 *   editMode={editMode}
 *   dirty={dirty}
 * />
 * ```
 */
export const ElementTabsV2 = <TTab extends string>({
	tabs,
	selectedTab,
	onTabChange,
	editMode,
	dirty: _dirty,
	node,
}: ElementTabsV2Props<TTab>) => {
	const { t } = useAppContext()
	const user = useAppStore((state) => state.auth.user)

	// Filter visible tabs
	const visibleTabs = useMemo(
		() => tabs.filter((tab) => tab.condition !== false),
		[tabs],
	)

	// Tab order for keyboard navigation
	const tabOrder = useMemo(
		() => visibleTabs.map((tab) => tab.id),
		[visibleTabs],
	)

	const tabIndex = tabOrder.indexOf(selectedTab)
	const currentIndex = tabIndex >= 0 ? tabIndex : 0

	// Keyboard navigation
	useTabKeyboardNav({
		tabs: tabOrder,
		currentIndex,
		onTabChange,
	})

	const handleChange = useCallback(
		(_event: SyntheticEvent, newValue: number) => {
			onTabChange(tabOrder[newValue])
		},
		[onTabChange, tabOrder],
	)

	// Status chip - only when not editing (header shows unified edit status in edit mode)
	const getStatusChip = () => {
		if (editMode) return null
		const nodeAsStructureDto = node as unknown as StructureDto
		const locked = isLocked(nodeAsStructureDto)
		const lockedByMe = isLockedByMe(nodeAsStructureDto, user)

		if (locked && !lockedByMe) {
			return {
				label: t('STATUS_LOCKED') || 'Locked',
				color: 'error' as const,
				icon: <LockIcon />,
			}
		}
		return {
			label: t('STATUS_VIEW_MODE') || 'View',
			color: 'default' as const,
			icon: <VisibilityIcon />,
		}
	}

	const statusChip = getStatusChip()

	return (
		<Box className="flex flex-col flex-grow overflow-hidden">
			<Box
				sx={{
					borderBottom: 1,
					borderColor: 'divider',
					display: 'flex',
					alignItems: 'center',
				}}
			>
				<Tabs
					value={currentIndex}
					onChange={handleChange}
					variant="scrollable"
					scrollButtons="auto"
					aria-label={t('ELEMENT_DETAIL_TABS')}
					sx={{ flex: 1 }}
				>
					{visibleTabs.map((tab, index) => (
						<Tab
							key={tab.id}
							label={t(tab.labelKey)}
							icon={tab.icon as React.ReactElement}
							iconPosition="start"
							{...a11yProps(index)}
						/>
					))}
				</Tabs>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
					{statusChip && (
						<Chip
							size="small"
							label={statusChip.label}
							color={statusChip.color}
							icon={statusChip.icon}
							variant="outlined"
						/>
					)}
					<KeyboardShortcutsHelp />
				</Box>
			</Box>

			<Box className="flex flex-col flex-grow min-h-0 overflow-hidden">
				{visibleTabs.map((tab, index) => (
					<MuiTabPanel key={tab.id} value={currentIndex} index={index} isTable>
						{tab.component}
					</MuiTabPanel>
				))}
			</Box>
		</Box>
	)
}

